import { clamp } from '$lib/utils';
import { smoothOut, springOut } from '@metafy/tailwind-config/ease';

// State to prevent awkward scrolling
let touchStartTime = 0;
let initialTouchY = 0;
let previousTouchY = 0;
let isScrolling = false;
let isInteracting = false;
let isTouchMoved = false;

/**
 * @description Set the initial state of the touch event
 * @param event
 */
function setInitialState(event: TouchEvent) {
  touchStartTime = Date.now();
  initialTouchY = event.touches[0].clientY;
  previousTouchY = initialTouchY;
}

/**
 * @ description Prevent the sheet from closing when scrolling
 * @param event
 * @param scrollerEl
 */
export function scrollerSheetPanel(scrollerEl: HTMLElement) {
  const scrollElement: HTMLElement = scrollerEl;
  function handleScrollEnd() {
    isScrolling = false;
  }
  function handleScrollStart(event) {
    isScrolling = false;
    if (scrollElement.scrollHeight > scrollElement.clientHeight && scrollElement.scrollTop > 0) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      isScrolling = true;
    }
    scrollElement.addEventListener('touchend', handleScrollEnd, { once: true });
  }
  scrollElement?.addEventListener('touchstart', handleScrollStart, { capture: true });
}

/**
 * @description This directive is used to create a sheet panel from a grabTarget,
 *  that can be dragged down on touch devices with inertia to close the panel.
 * @param grabTarget The element that will be used to drag the sheet panel down (generally a tab at top of the sheet panel)
 * @param options {Object} An object containing the options for the sheet panel
 * @param options.onClose A callback function that will be called when the sheet panel is closed
 * @returns
 */
export const closeSheetPanel = (grabTarget: HTMLElement, options: { onClose: Function }) => {
  const sheetElement: HTMLElement | null = grabTarget.closest('.sheet') || grabTarget.closest('dialog');

  if (!grabTarget || !sheetElement) {
    return;
  }

  /** The minimum velocity required to close the sheet panel */
  const closeVelocityThreshold = 0.35;
  /** The maximum duration of the closing animation, it will scale according to the velocity of the touch movement */
  const maxDuration = 500;

  const getCloseThreshold = () => Math.min(sheetElement.clientHeight / 3, 150);
  const getSheetHeight = () => sheetElement.clientHeight;

  let deltaDirection = 0;
  let dragDistance = 0;
  let sheetHeight = getSheetHeight();
  let closeThreshold = getCloseThreshold();
  let frameScheduled = false;
  let closeTimer;

  // Add touch event listeners
  grabTarget.addEventListener('touchstart', handleTouchStart, { capture: true });

  /**
   * @description Check if the target element is a form element or has the [data-no-drag-close] attribute
   * @param target
   * @returns boolean
   */
  function isDisableDragging(target) {
    const isManualDisabled = target.closest('[data-no-drag-close]') || target.closest('[data-no-drag-close] *');
    const isFormElementActive = target.closest('input, textarea, select, [contenteditable]');
    return isManualDisabled || isFormElementActive;
  }

  function handleTouchStart(event: TouchEvent) {
    if (!sheetElement || isScrolling || isInteracting) return;

    // Prevent the drag closing if the target is a form element
    // or has the [data-no-drag-close] attribute
    const target = event.target as HTMLElement;
    if (isDisableDragging(target)) return;

    // reset the variables
    setInitialState(event);
    isTouchMoved = false;
    isInteracting = true;
    sheetElement.style.removeProperty('transition');
    sheetHeight = getSheetHeight();
    closeThreshold = getCloseThreshold();
    frameScheduled = false;
    dragDistance = 0;
    // Add touch move and touch end event listeners
    grabTarget.addEventListener('touchmove', handleTouchMove, { capture: true });
    grabTarget.addEventListener('touchend', handleTouchEnd);
  }

  function handleTouchMove(event: TouchEvent) {
    if (frameScheduled || !sheetElement || isScrolling) return;
    event.stopImmediatePropagation();
    event.stopPropagation();
    isInteracting = true;
    frameScheduled = true;
    window.requestAnimationFrame(() => {
      frameScheduled = false;
    });

    // Calculate the distance the touch has moved
    const touchY = event.touches[0].clientY;
    deltaDirection = touchY - previousTouchY;
    dragDistance = touchY - initialTouchY;

    // fade out the sheet element as it is dragged down past 40% of its height
    const opacity = clamp(1.4 - (dragDistance / sheetHeight) * 1.3, 0.2, 1);
    sheetElement.style.setProperty('--sheet-opacity', opacity.toString());

    if (!isTouchMoved) {
      sheetElement.style.transition = 'none';
      isTouchMoved = true;
    }

    // Move the sheet element along with the touch movement
    if (dragDistance >= 0) {
      // sheetElement.style.transform = `translateY(${dragDistance}px)`;
      sheetElement.style.setProperty('--sheet-y', `${dragDistance}px`);
    }

    // Store the previous touch position
    previousTouchY = touchY;
  }

  function isOnScreen() {
    const y = sheetElement?.getBoundingClientRect().y;
    if (y && y < screen.height) {
      requestAnimationFrame(isOnScreen);
    } else {
      closeSheet();
    }
  }

  function handleTouchEnd() {
    if (!sheetElement || isScrolling) return;
    isInteracting = false;
    isTouchMoved = false;

    // Calculate the elapsed time since touch start
    const touchEndTime = Date.now();
    const elapsedTime = touchEndTime - touchStartTime;

    // Calculate the velocity of the touch movement
    const velocity = dragDistance / elapsedTime;

    // clear the transition
    sheetElement.style.removeProperty('transition');

    // If the sheet element has been dragged past the close threshold
    // or has enough velocity to close, we close it. But not if the direction is up.
    if (deltaDirection > 0 && (dragDistance >= closeThreshold || velocity >= closeVelocityThreshold)) {
      // Calculate the animation duration based on velocity
      const duration = Math.min(Math.abs(dragDistance / velocity) * 2, 600);
      // Apply the animation duration and transform style for the closing animation
      sheetElement.style.transition = `transform ${duration}ms ${smoothOut}, opacity ${duration}ms ${smoothOut}`;
      sheetElement.style.setProperty('--sheet-y', `105%`);
      sheetElement.style.setProperty('--sheet-opacity', '0');
      // Schedule the sheet element to be removed from the DOM after the animation duration
      closeTimer = setTimeout(closeSheet, duration);
      // Run a check to see if the sheet element is still on screen
      // which allows to kill the animation early and just feels better
      isOnScreen();
    } else if (dragDistance > 0) {
      // If not enough inertia/distance, open the sheet element again
      sheetElement.style.transition = `transform ${maxDuration}ms ${springOut}, opacity ${maxDuration}ms ${smoothOut}`;
      sheetElement.style.setProperty('--sheet-y', `0px`);
      sheetElement.style.setProperty('--sheet-opacity', '1');
    }
    // reset
    dragDistance = 0;
    grabTarget.removeEventListener('touchmove', handleTouchMove, { capture: true });
    grabTarget.removeEventListener('touchend', handleTouchEnd);
  }

  function closeSheet() {
    if (!sheetElement || isInteracting) return;
    clearTimeout(closeTimer);
    sheetElement.style.removeProperty('--sheet-y');
    sheetElement.style.removeProperty('--sheet-opacity');
    sheetElement.style.removeProperty('transition');
    if (typeof options.onClose === 'function') {
      options.onClose();
    }
  }

  return {
    destroy() {
      grabTarget.removeEventListener('touchstart', handleTouchStart, { capture: true });
      grabTarget.removeEventListener('touchmove', handleTouchMove, { capture: true });
      grabTarget.removeEventListener('touchend', handleTouchEnd);
    }
  };
};
